import { create } from 'zustand';
import {
  getUsers,
  getLearningItems,
  getAssignments,
  getAssignmentDetail,
  getDepartments,
  createAssignment as createAssignmentApi,
  User,
  LearningItem,
  Assignment,
  UserProgress,
  CreateAssignmentPayload,
} from '@/services/SuperAdminService/LearningAssignmentService';

interface LearningAssignmentState {
  users: User[] | null;
  learningItems: LearningItem[] | null;
  assignments: Assignment[] | null;
  userProgress: UserProgress[] | null;
  departments: string[] | null;
  loading: boolean;
  creating: boolean;
  error: string | null;

  fetchUsers: () => Promise<void>;
  fetchLearningItems: () => Promise<void>;
  fetchAssignments: () => Promise<void>;
  fetchDepartments: () => Promise<void>;
  fetchUserProgress: (assignmentId: string) => Promise<void>;
  createAssignment: (payload: CreateAssignmentPayload) => Promise<{ success: boolean; error?: string }>;
}

const useLearningAssignmentStore = create<LearningAssignmentState>((set, get) => ({
  users: null,
  learningItems: null,
  assignments: null,
  userProgress: null,
  departments: null,
  loading: false,
  creating: false,
  error: null,

  fetchUsers: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getUsers();
      set({ users: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchLearningItems: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getLearningItems();
      set({ learningItems: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchAssignments: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getAssignments();
      set({ assignments: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchDepartments: async () => {
    try {
      const response = await getDepartments();
      set({ departments: response.data || [] });
    } catch (error: any) {
      // Non-fatal: the department filter just stays empty.
      set({ departments: [] });
    }
  },

  fetchUserProgress: async (assignmentId: string) => {
    try {
      set({ loading: true, error: null });
      const response = await getAssignmentDetail(assignmentId);
      set({ userProgress: response.data?.users || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  createAssignment: async (payload) => {
    try {
      set({ creating: true, error: null });
      const res = await createAssignmentApi(payload);
      set({ creating: false });
      if (!res.success) {
        const message = res.error?.message || 'Failed to create assignment';
        set({ error: message });
        return { success: false, error: message };
      }
      // Refresh the history list so the new assignment shows up immediately.
      await get().fetchAssignments();
      return { success: true };
    } catch (error: any) {
      set({ creating: false, error: error.message });
      return { success: false, error: error.message };
    }
  },
}));

export default useLearningAssignmentStore;
